var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import * as pc from "playcanvas";
import { CupFillGhosts } from "./cup-fill-ghosts";
import { CupFillPanel } from "./cup-fill-panel";
import { useAppDispatch, useAppSelector, actions, selectors } from "store";
import { usePlaycanvasApplicationContext } from "providers/playcanvas-application";
import { isPointerOnCanvas, screenToWorldAtY } from "hooks/playcanvas/picker";
import { computeCupGridFill } from "3d/cup-grid-fill";
/**
 * Everything the user sees of the "fill an area with cups" tool while it is
 * armed: the panel explaining it and the ghost cups under the pointer. The
 * picker owns the click that places them (`fillAreaWithGripPositions`); both
 * read the same layout selector and run the same grid function, so the ghosts
 * are exactly what a click creates.
 *
 * Renders nothing while the tool is off — the tool has no presence in the
 * scene of its own, only through its anchor cup's toolbar button.
 */
export var CupFillOverlay = function (_a) {
    var app = _a.app;
    var layout = useAppSelector(selectors.selectCupFillLayout);
    if (!layout)
        return null;
    return _jsx(ArmedCupFillOverlay, { app: app, layout: layout });
};
var ArmedCupFillOverlay = function (_a) {
    var _b;
    var app = _a.app, layout = _a.layout;
    var dispatch = useAppDispatch();
    var isCameraMoving = usePlaycanvasApplicationContext().isCameraMoving;
    var camera = (_b = app.root.findByName("Camera")) === null || _b === void 0 ? void 0 : _b.camera;
    /* Where the pointer last was on the cup plane. Moves off the canvas itself
     * (over the panel or the topbar, say) are ignored so the preview holds still
     * while the gap is being edited. */
    var _c = __read(useState(null), 2), pointer = _c[0], setPointer = _c[1];
    /* Bumped when the view changes under a resting pointer, so the ghosts get
     * re-projected: a wheel zoom, or the camera coming to rest. */
    var _d = __read(useState(0), 2), viewVersion = _d[0], setViewVersion = _d[1];
    useEffect(function () {
        if (!camera)
            return;
        var onMove = function (event) {
            if (!isPointerOnCanvas(app, event))
                return;
            var next = screenToWorldAtY(camera, event.x, event.y, layout.planeY);
            setPointer(function (previous) {
                return previous && previous.x === next.x && previous.z === next.z
                    ? previous
                    : next;
            });
        };
        var onWheel = function () { return setViewVersion(function (version) { return version + 1; }); };
        app.mouse.on(pc.EVENT_MOUSEMOVE, onMove);
        app.mouse.on(pc.EVENT_MOUSEWHEEL, onWheel);
        return function () {
            app.mouse.off(pc.EVENT_MOUSEMOVE, onMove);
            app.mouse.off(pc.EVENT_MOUSEWHEEL, onWheel);
        };
    }, [app, camera, layout.planeY]);
    useEffect(function () {
        if (!isCameraMoving)
            setViewVersion(function (version) { return version + 1; });
    }, [isCameraMoving]);
    // Esc disarms the tool, like closing the panel.
    useEffect(function () {
        var onKeyDown = function (event) {
            if (event.key === "Escape")
                dispatch(actions.stopCupFill());
        };
        window.addEventListener("keydown", onKeyDown);
        return function () { return window.removeEventListener("keydown", onKeyDown); };
    }, [dispatch]);
    var fill = useMemo(function () {
        return pointer
            ? computeCupGridFill({
                anchor: layout.anchor,
                target: pointer,
                pitch: layout.pitch,
                limit: layout.limit,
            })
            : null;
    }, [pointer, layout.anchor, layout.pitch, layout.limit]);
    return (_jsxs(_Fragment, { children: [camera && fill && pointer && !isCameraMoving && (_jsx(CupFillGhosts, { camera: camera, layout: layout, fill: fill, pointer: pointer, viewVersion: viewVersion })), _jsx(CupFillPanel, { layout: layout, fill: fill, onGapChange: function (gap) { return dispatch(actions.setCupFillGap(Math.max(0, gap))); }, onCancel: function () { return dispatch(actions.stopCupFill()); } })] }));
};
